// Attachments (architecture plan §13.3; build plan P12): the intent, the finalize, the view, and
// the download slot. The size ceiling and the type allowlist are the spike 4 proposals; the
// magic-bytes table is what finalization checks the declared type against.

import { z } from "zod";

/** Spike 4 gap 5: proposed 25 MB, enforced by the presign scope and again at finalization. */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
/** How long an upload slot stays open. */
export const ATTACHMENT_INTENT_MINUTES = 10;
/** How long a download URL lives. */
export const ATTACHMENT_DOWNLOAD_SECONDS = 60;

export const ALLOWED_ATTACHMENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
] as const;
export const AttachmentContentTypeSchema = z.enum(ALLOWED_ATTACHMENT_TYPES);
export type AttachmentContentType = z.infer<typeof AttachmentContentTypeSchema>;

export const ATTACHMENT_SCAN_STATES = [
  "pending",
  "scanning",
  "clean",
  "infected",
  "failed",
] as const;
export const AttachmentScanStateSchema = z.enum(ATTACHMENT_SCAN_STATES);
export type AttachmentScanState = z.infer<typeof AttachmentScanStateSchema>;

const Sha256Base64 = z.string().regex(/^[A-Za-z0-9+/]{43}=$/, "expected a base64 SHA-256 digest");

export const AttachmentIntentBodySchema = z
  .object({
    filename: z.string().trim().min(1).max(200),
    contentType: AttachmentContentTypeSchema,
    size: z.number().int().positive().max(ATTACHMENT_MAX_BYTES),
    /** Spike 4 gap 2: SHA-256 over the file, base64, computed client-side before upload. */
    sha256: Sha256Base64,
  })
  .strict();
export type AttachmentIntentBody = z.infer<typeof AttachmentIntentBodySchema>;

export const AttachmentIntentResponseSchema = z.object({
  id: z.uuid(),
  upload: z.object({
    url: z.url(),
    method: z.literal("PUT"),
    headers: z.record(z.string(), z.string()),
    expiresAt: z.iso.datetime(),
  }),
});
export type AttachmentIntentResponse = z.infer<typeof AttachmentIntentResponseSchema>;

export const AttachmentSchema = z.object({
  id: z.uuid(),
  filename: z.string(),
  contentType: z.string(),
  size: z.number().int(),
  scanState: AttachmentScanStateSchema,
  /** True only when the row is `clean`: the one state a download is issued for. */
  downloadable: z.boolean(),
  /** Why a file was not accepted, when it was not. Never a scanner internal. */
  rejectedReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

export const AttachmentListSchema = z.object({ items: z.array(AttachmentSchema) });

export const AttachmentDownloadSchema = z.object({
  url: z.url(),
  expiresAt: z.iso.datetime(),
  filename: z.string(),
});
export type AttachmentDownload = z.infer<typeof AttachmentDownloadSchema>;

/**
 * Magic bytes per allowed type (§13.3 additional rules). A file whose bytes contradict its
 * declared type fails at finalization, before a scan pass is spent on it.
 */
const MAGIC: ReadonlyArray<{ type: AttachmentContentType; test: (b: Uint8Array) => boolean }> = [
  { type: "image/png", test: (b) => starts(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]) },
  { type: "image/jpeg", test: (b) => starts(b, [0xff, 0xd8, 0xff]) },
  { type: "image/gif", test: (b) => ascii(b, 0, "GIF87a") || ascii(b, 0, "GIF89a") },
  { type: "image/webp", test: (b) => ascii(b, 0, "RIFF") && ascii(b, 8, "WEBP") },
  { type: "application/pdf", test: (b) => ascii(b, 0, "%PDF-") },
];

function starts(b: Uint8Array, sig: number[]): boolean {
  return sig.every((v, i) => b[i] === v);
}
function ascii(b: Uint8Array, offset: number, s: string): boolean {
  for (let i = 0; i < s.length; i++) if (b[offset + i] !== s.charCodeAt(i)) return false;
  return true;
}

/** The type the first bytes prove, or `text/plain` for bytes with no binary signature and no NUL. */
export function sniffContentType(prefix: Uint8Array): AttachmentContentType | "unknown" {
  for (const m of MAGIC) if (m.test(prefix)) return m.type;
  // Executables and archives are never text: MZ, ELF, Mach-O, ZIP, and NUL bytes.
  if (
    ascii(prefix, 0, "MZ") ||
    starts(prefix, [0x7f, 0x45, 0x4c, 0x46]) ||
    starts(prefix, [0x50, 0x4b, 0x03, 0x04])
  )
    return "unknown";
  if (starts(prefix, [0xcf, 0xfa, 0xed, 0xfe]) || starts(prefix, [0xfe, 0xed, 0xfa, 0xcf]))
    return "unknown";
  if (prefix.length === 0) return "unknown";
  for (const byte of prefix) if (byte === 0) return "unknown";
  return "text/plain";
}

/** A filename safe to store, to send back, and to name on a card: no path, no control characters. */
export function sanitizeFilename(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? "file";
  const cleaned = Array.from(base)
    .filter((c) => c.charCodeAt(0) >= 32 && !'"<>|:*?'.includes(c))
    .join("")
    .trim();
  return (cleaned || "file").slice(0, 120);
}
